import { Flex, Heading, Button, Text, Spinner } from "@radix-ui/themes";
import { InstructionCard } from "./instructions-card";
import { BrandLogo } from "../../components/brand-logo";
import { LangSelector } from "./lang-selector";
import { useTranslation } from "react-i18next";
import LeftDoubleTap from "../../assets/left-double-tap.svg?react";
import ExitIcon from "../../assets/exit.svg?react";

type UnmannedExitInstructionsProps = {
  storeKey: string;
  loading: boolean;
  onCheckout: () => void;
};

export function UnmannedExitInstructions({
  storeKey,
  loading,
  onCheckout,
}: UnmannedExitInstructionsProps) {
  // TODO: We should derive this logic from backend
  // Backend knows which exit turnstile automatically opens, which one doesn't
  const AUTO_EXIT_TURNSTILE_STORE_KEYS = ["st-1"];

  return (
    <Flex direction="column" className="h-full">
      <Flex
        justify="between"
        className="text-[#232323] bg-white px-6 pt-6 pb-4"
      >
        <BrandLogo />

        <LangSelector />
      </Flex>

      {AUTO_EXIT_TURNSTILE_STORE_KEYS.includes(storeKey) ? (
        <AutoTurnstileOpenExit />
      ) : (
        <ExplicitExit loading={loading} onCheckout={onCheckout} />
      )}
    </Flex>
  );
}

function ExplicitExit({
  loading,
  onCheckout,
}: Omit<UnmannedExitInstructionsProps, "storeKey">) {
  const { t } = useTranslation();

  return (
    <>
      <Flex
        direction="column"
        flexGrow="1"
        gap="3"
        className="bg-white px-6 pb-4 rounded-b-3xl"
      >
        <Flex className="h-16" justify="center" align="center">
          <Heading className="!text-[2.625rem] !font-semibold">
            {t("thankYouForShopping")}
          </Heading>
        </Flex>

        <Flex className="gap-3 flex-1">
          <InstructionCard
            image="/unmanned/ext-01.webp"
            title={
              <Flex className="min-h-[84px]" px="2">
                <Text size="5" weight="medium" align="center">
                  1. {t("exitIns1")}
                </Text>
              </Flex>
            }
            imgClassName="max-h-[395px] -translate-y-2"
          />
          <InstructionCard
            image="/unmanned/ext-02.webp"
            title={
              <Flex className="min-h-[84px]" px="2">
                <Text size="5" weight="medium" align="center">
                  2. {t("exitIns2")}
                </Text>
              </Flex>
            }
            imgClassName="max-h-[395px] scale-[1.25] -translate-x-6 -translate-y-2"
          />
          <InstructionCard
            image="/unmanned/ext-03.webp"
            title={
              <Flex className="min-h-[84px]" px="2">
                <Text size="5" weight="medium" align="center">
                  3. {t("exitIns3")}
                </Text>
              </Flex>
            }
            imgClassName="max-h-[395px] scale-[0.95]"
          />
        </Flex>
      </Flex>
      <Flex justify="center" px="5" pb="4">
        <Button
          onClick={onCheckout}
          disabled={loading}
          size="4"
          variant="classic"
          color="red"
          radius="large"
          className={`!min-h-24 !text-4xl !w-full !pointer-events-auto !rounded-3xl !bg-[#CE2C31] !text-white transition active:scale-95 !duration-150 tappable`}
        >
          {loading ? (
            <Spinner size="3" className="!size-10" />
          ) : (
            <Flex align="center" className="whitespace-nowrap !relative">
              <ExitIcon className="size-14 mr-6" />
              {t("checkOut")}

              <div className="-scale-x-100 absolute -right-[240px] top-1/2 -translate-y-1/2 h-24">
                <LeftDoubleTap />
              </div>
            </Flex>
          )}
        </Button>
      </Flex>
    </>
  );
}

function AutoTurnstileOpenExit() {
  const { t } = useTranslation();

  return (
    <>
      <Flex justify="center" mt="4">
        <img width={100} height={100} src="/unmanned/check-icon.jpg" />
      </Flex>
      <Flex
        direction="column"
        flexGrow="1"
        gap="8"
        pb="9"
        className="px-8 pb-4 rounded-b-3xl relative z-10"
      >
        <Flex className="h-16" justify="center" align="center">
          <Heading
            weight="medium"
            className="!text-[2.25rem] !font-medium"
            highContrast
          >
            {t("thankYouForPurchase")}
          </Heading>
        </Flex>
        <Flex className="h-16" justify="center" align="center" px="7">
          <Heading
            className="!text-[3.25rem] !leading-tight !font-bold !text-black"
            align="center"
            highContrast
          >
            {t("youCanLeaveStore")}
          </Heading>
        </Flex>
        <Flex className="h-16" justify="center" align="center">
          <Heading weight="medium" className="!text-[2.25rem] !font-medium">
            {t("seeYouAgain")}
          </Heading>
        </Flex>
      </Flex>

      <Flex className="overflow-hidden absolute bottom-0 left-0 z-0">
        <img src="/turnstile-success.png" className="translate-y-20" />
      </Flex>
    </>
  );
}
